import React, { useEffect, useState } from 'react';
import type { BrandTeamAgentLog } from '../../../../services/brandTeamService';
import { getAgentPersona } from '../constants';
import { Sparkles, Cpu } from 'lucide-react';

interface PremiumLiveActivityProps {
  logs: BrandTeamAgentLog[];
}

const PremiumLiveActivity: React.FC<PremiumLiveActivityProps> = ({ logs }) => {
  const [displayedLogs, setDisplayedLogs] = useState<BrandTeamAgentLog[]>([]);

  useEffect(() => {
    // Sort descending by time
    const sorted = [...logs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    // Only show top 4 to keep it compact but highly premium
    setDisplayedLogs(sorted.slice(0, 4));
  }, [logs]);

  if (displayedLogs.length === 0) return null;

  return (
    <div className="w-full relative rounded-2xl bg-white shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 overflow-hidden text-left">
      {/* Premium Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/80">
        <div className="flex items-center gap-3">
          <div className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-purple opacity-40"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-purple"></span>
          </div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-800 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-brand-purple" />
            Live Agent Activity
          </h3>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-brand-purple bg-brand-purple/10 px-3 py-1.5 rounded-full border border-brand-purple/20 shadow-inner">
          <Cpu className="w-3.5 h-3.5 animate-pulse" />
          PROCESSING
        </div>
      </div>

      {/* Activity List */}
      <div className="p-6 flex flex-col gap-6 relative">
        {/* Vertical Tracking Line */}
        <div className="absolute left-[44px] top-10 bottom-10 w-px bg-gradient-to-b from-brand-purple via-gray-200 to-transparent"></div>

        {displayedLogs.map((log, index) => {
          const persona = getAgentPersona(log.agentKey);
          const Icon = persona.icon;
          const isLatest = index === 0;

          return (
            <div 
              key={log.id} 
              className={`relative flex items-start gap-5 transition-all duration-700 ease-out transform origin-top
                ${isLatest ? 'opacity-100 translate-x-0 scale-100' : 'opacity-40 translate-x-0 scale-[0.98]'}`}
            >
              {/* Avatar Icon */}
              <div className="relative z-10 pt-1">
                <div className={`
                  flex items-center justify-center w-11 h-11 rounded-full bg-gradient-to-br ${persona.gradientFrom} ${persona.gradientTo}
                  shadow-md border-[3px] border-white
                  ${isLatest ? 'ring-4 ring-brand-purple/20 shadow-brand-purple/30 animate-pulse' : ''}
                `}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </div>

              {/* Content Card */}
              <div className={`
                flex-1 rounded-xl p-4 transition-all duration-500
                ${isLatest 
                  ? 'bg-gradient-to-br from-white to-gray-50 border border-brand-purple/20 shadow-[0_4px_20px_rgb(0,0,0,0.03)]' 
                  : 'bg-transparent border border-transparent hover:bg-gray-50/50'}
              `}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2.5">
                    <span className={`font-semibold text-sm ${isLatest ? 'text-gray-900' : 'text-gray-600'}`}>
                      {persona.name}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-md bg-gray-100/80 text-gray-500 font-medium tracking-wide uppercase">
                      {persona.role}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400 font-medium">
                    {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                </div>
                <p className={`text-[13px] leading-relaxed ${isLatest ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                  {log.summary}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PremiumLiveActivity;
